import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MediaBox } from "@/components/MediaBox";
import { fetchWorkPosts } from "@/lib/api";
import { toWorkItem } from "@/lib/workItem";
import { GRID, WORK } from "@/constants/testIds";

const GROUPS = [
  { key: "widescreen", label: "Widescreen", ratio: "16 / 9", initialSize: 6, cols: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" },
  { key: "vertical", label: "Vertical", ratio: "9 / 16", initialSize: 8, cols: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" },
  { key: "square", label: "Square", ratio: "1 / 1", initialSize: 6, cols: "grid-cols-2 sm:grid-cols-2 lg:grid-cols-3" },
  { key: "unknown", label: "More Work", ratio: "1 / 1", initialSize: 8, cols: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" },
];

const useBucket = (bucketKey, filters, pageSize, groupTotal) => {
  const filterKey = JSON.stringify(filters);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const seenRef = useRef(new Set());

  useEffect(() => {
    let cancelled = false;
    seenRef.current = new Set();
    setItems([]);
    setPage(0);
    if (!groupTotal) return;
    setLoading(true);
    fetchWorkPosts({ ...filters, coverLayout: bucketKey, page: 1, pageSize }).then((data) => {
      if (cancelled) return;
      const fresh = data.items.map(toWorkItem);
      fresh.forEach((it) => seenRef.current.add(it.slug));
      setItems(fresh);
      setPage(1);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, bucketKey, groupTotal]);

  const loadMore = async () => {
    setLoading(true);
    const nextPage = page + 1;
    const data = await fetchWorkPosts({ ...filters, coverLayout: bucketKey, page: nextPage, pageSize });
    const fresh = data.items.filter((it) => !seenRef.current.has(it.shortcode)).map(toWorkItem);
    fresh.forEach((it) => seenRef.current.add(it.slug));
    setItems((prev) => [...prev, ...fresh]);
    setPage(nextPage);
    setLoading(false);
  };

  return { items, loading, hasMore: items.length < groupTotal, loadMore };
};

const GroupSection = ({ g, filters, groupTotal, searchString, linkBase }) => {
  const { items, loading, hasMore, loadMore } = useBucket(g.key, filters, g.initialSize, groupTotal);
  if (!groupTotal) return null;

  return (
    <section data-testid={WORK.groupSection(g.key)}>
      <div className="flex items-baseline justify-between gap-3 border-b border-[#2A2A2A] pb-3 mb-5 sm:mb-6">
        <h2 className="text-base sm:text-lg tracking-wide uppercase text-[#F5F5F0]">{g.label}</h2>
        <span className="text-[11px] uppercase tracking-widest text-[#555550] whitespace-nowrap">{groupTotal}</span>
      </div>
      <div className={`grid ${g.cols} gap-4 sm:gap-5`}>
        {items.map((p) => (
          <Link
            key={p.slug}
            to={`${linkBase}/${p.slug}`}
            state={{ fromSearch: searchString }}
            data-testid={GRID.card(p.slug)}
            className="group block relative border border-[#2A2A2A] hover:border-[#CCFF00] transition-colors duration-300"
          >
            <div className="relative w-full bg-[#050505]" style={{ aspectRatio: g.ratio }}>
              <MediaBox project={p} className="absolute inset-0" fit="contain" />
            </div>
            <div className="p-2.5 sm:p-3 flex items-center justify-between gap-2 text-[10px] sm:text-xs tracking-widest uppercase text-[#F5F5F0] group-hover:text-[#CCFF00] transition-colors">
              <span className="truncate">{p.title}</span>
              <span className="text-[#888880] shrink-0">{p.year}</span>
            </div>
          </Link>
        ))}
      </div>
      {hasMore && (
        <div className="flex justify-center mt-6">
          <button
            type="button"
            onClick={loadMore}
            disabled={loading}
            data-testid={`work-load-more-${g.key}`}
            className="text-xs uppercase tracking-widest px-4 min-h-[40px] border border-[#2A2A2A] text-[#888880] hover:text-[#CCFF00] hover:border-[#CCFF00] disabled:opacity-40 transition-colors"
          >
            {loading ? "Loading…" : `Load More ${g.label}`}
          </button>
        </div>
      )}
    </section>
  );
};

export const WorkGroupedGrid = ({ filters, groupCounts, searchString, linkBase = "/film" }) => {
  if (!groupCounts) return null;
  const anyResults = groupCounts.total > 0;
  if (!anyResults) return null;

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-8 py-8 sm:py-10 space-y-12 sm:space-y-16" data-testid={GRID.container}>
      {GROUPS.map((g) => (
        <GroupSection
          key={g.key}
          g={g}
          filters={filters}
          groupTotal={groupCounts[g.key] || 0}
          searchString={searchString}
          linkBase={linkBase}
        />
      ))}
    </div>
  );
};

export default WorkGroupedGrid;
